<template>
    <div class="fb-inline fb-shell" :class="$store.state.darkMode ? 'dark' : 'light'">
        <FileBrowserControls @credentialChange="onCredentialChange" @bucketChange="onSelectBucket"
            @refresh="onRefresh" />
        <div v-if="error" class="fb-notice fb-notice-danger mt-3">
            <i class="fa-solid fa-circle-exclamation"></i>
            <span>{{ error }}</span>
        </div>
        <div v-if="truncated" class="fb-notice mt-3">
            <i class="fa-solid fa-circle-info"></i>
            <span>Showing the first {{ maxResults.toLocaleString() }} objects. Open a folder to narrow the
                listing.</span>
        </div>
        <div v-if="selectedBucket" class="fb-panel mt-3">
            <div class="fb-panel-head">
                <FileBrowserPath :breadcrumbItems="$store.state.breadcrumbs" :showRoute="false"
                    @onBreadcrumbClick="onBreadcrumbClick" />
            </div>
            <FileBrowserTable tableId="fileBrowserTable" :data="filesList" :loading="loading" @open="open" />
        </div>
    </div>
</template>

<script>
/*
 * Inline file browser used by the file selection modal and the flow set wizard. Same
 * pail endpoints as the file browser page, but navigation stays in component state
 * instead of the router.
 */
import { mapActions } from 'vuex';
import FileBrowserControls from "../filebrowser/FileBrowserControls.vue";
import FileBrowserPath from "../filebrowser/FileBrowserPath.vue";
import FileBrowserTable from "../filebrowser/FileBrowserTable.vue";
import {
    MAX_LIST_RESULTS,
    MAX_PARSABLE_FILE_SIZE,
    buildStoragePath,
    isParsableFile,
    normalizeObjects,
    parseStoragePath,
} from "../../utils/fileBrowser";

export default {
    components: { FileBrowserControls, FileBrowserPath, FileBrowserTable },
    data() {
        return {
            filesList: [],
            loading: false,
            selectedBucket: null,
            credential: null,
            scheme: null,
            error: null,
            truncated: false,
            maxResults: MAX_LIST_RESULTS,
        };
    },
    props: {
        generateURL: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        folderPath() {
            return this.$store.state.amdp.folderPath;
        },
    },
    methods: {
        ...mapActions({
            getFileBrowserList: "amdp/getFileBrowserList",
            getPresignURL: "amdp/getPresignURL",
        }),
        onCredentialChange({ name, scheme }) {
            this.credential = name;
            this.scheme = scheme;
            this.selectedBucket = null;
            this.filesList = [];
            this.error = null;
        },
        onSelectBucket(bucket) {
            this.selectedBucket = bucket;
            this.$store.dispatch('setBreadcrumbs', [{ name: bucket, route: "" }]);
            const path = buildStoragePath(this.scheme, bucket);
            this.$store.dispatch('amdp/setFolderPath', path);
            this.fetchFileBrowserList(path);
        },
        // A folder descends in place and extends the trail; a file is handed to the caller.
        open(row) {
            if (!row) return;
            if (row.is_dir) {
                this.$store.dispatch('setBreadcrumbs', [...this.$store.state.breadcrumbs, {
                    name: row.folderName !== "" ? row.folderName : "/",
                    route: "",
                }]);
                const path = `${this.folderPath}${row.folderName}/`;
                this.$store.dispatch('amdp/setFolderPath', path);
                this.fetchFileBrowserList(path);
                return;
            }
            this.selectFile(row);
        },
        onBreadcrumbClick() {
            this.fetchFileBrowserList(this.folderPath);
        },
        onRefresh() {
            this.fetchFileBrowserList(this.folderPath);
        },
        async fetchFileBrowserList(folderPath) {
            const parsed = parseStoragePath(folderPath);
            if (!parsed || !this.credential) return;
            this.error = null;
            this.truncated = false;
            this.filesList = [];
            this.loading = true;
            let response;
            try {
                response = await this.getFileBrowserList({
                    credential: this.credential,
                    bucket: parsed.bucket,
                    prefix: parsed.key,
                    maxResults: MAX_LIST_RESULTS,
                });
            } catch (error) {
                this.filesList = [];
                this.loading = false;
                this.error = error.response?.data?.error || error.message;
                return;
            }
            this.filesList = normalizeObjects(response.data?.items, parsed.key);
            this.truncated = !!response.data?.nextPageToken;
            this.loading = false;
        },
        selectFile(file) {
            const parsed = parseStoragePath(this.folderPath);
            if (!parsed) return;
            // names are relative to the folder in view, so the key is simply appended
            const key = `${parsed.key}${file.folderName || file.name}`;
            const path = buildStoragePath(parsed.scheme, parsed.bucket, key);
            // avro/parquet are previewed through pail's parser, which reads the whole
            // object - reject oversized ones here rather than after the round trip
            if (isParsableFile(key) && file.size > MAX_PARSABLE_FILE_SIZE) {
                this.error = `${file.folderName} is larger than ${MAX_PARSABLE_FILE_SIZE / (1024 * 1024)}MB and cannot be previewed.`;
                return;
            }
            this.error = null;
            // let the caller show the selection while the presigned URL is fetched
            this.$emit('onFileClicked', { name: path });
            if (this.generateURL || key.endsWith('.json') || key.endsWith('.csv')) {
                this.fetchPresignURL(parsed, key, path, file);
            } else {
                this.$emit('onFileSelect', {
                    presignedUrl: null,
                    name: path,
                    credential: this.credential,
                    size: file.size,
                });
            }
        },
        async fetchPresignURL(parsed, key, path, file) {
            this.loading = true;
            let response;
            try {
                response = await this.getPresignURL({
                    credential: this.credential,
                    bucket: parsed.bucket,
                    key,
                });
            } catch (error) {
                this.loading = false;
                this.error = error.response?.data?.error || error.message;
                return;
            }
            this.$emit('onFileSelect', {
                presignedUrl: response.data?.url,
                name: path,
                credential: this.credential,
                size: file?.size,
            });
            this.loading = false;
        },
    },
    emits: ['onFileSelect', 'onFileClicked']
}
</script>

<style>
@import "../filebrowser/fileBrowser.css";
</style>
